/**
 * Phase 19 / Task 19.3 (DEC-325) — data for the Management dashboard.
 *
 * GET /v1/dashboards/management. Types mirror packages/api/src/routes/dashboards/management.ts
 * and services/metrics/management-dashboard.ts (copied by hand, like every web-dispatch hook).
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';

export interface PeriodKpis {
  readonly from: string;
  readonly to: string;
  readonly revenueCents: number;
  readonly invoicesSent: number;
  readonly loads: number;
  readonly bbl: string;
}

export interface TrendPoint {
  readonly day: string;
  readonly bbl: string;
}

export interface TopCustomer {
  readonly haulerOperatorId: string | null;
  readonly operatorId: string | null;
  readonly name: string;
  readonly loads: number;
  readonly bbl: string;
  readonly sharePct: number | null;
}

export interface TopSite {
  readonly locationId: string | null;
  readonly wellId: string | null;
  readonly name: string;
  readonly loads: number;
  readonly bbl: string;
  readonly sharePct: number | null;
}

export interface ManagementDashboardResponse {
  readonly generatedAt: string;
  readonly companyTimezone: string;
  readonly history: { readonly lastRunAt: string | null; readonly coveredThrough: string | null };
  readonly current: PeriodKpis;
  readonly previous: PeriodKpis;
  readonly changes: {
    readonly revenuePct: number | null;
    readonly loadsPct: number | null;
    readonly bblPct: number | null;
  };
  readonly trend: {
    readonly current: readonly TrendPoint[];
    readonly previous: readonly TrendPoint[];
  };
  readonly topCustomers: readonly TopCustomer[];
  readonly topSites: readonly TopSite[];
  readonly utilization: {
    readonly trucksUsed: number;
    readonly trucksActive: number;
    readonly truckPct: number | null;
    readonly driversWithLoads: number;
    readonly driversActive: number;
    readonly driverPct: number | null;
    readonly avgActiveSecsPerDriver: number | null;
  };
  readonly compliance: { readonly expired: number; readonly expiringSoon: number };
  readonly unbilledCents: number;
}

export function useManagementDashboard(
  apiOpts: ReturnType<typeof useApiOpts>,
  from: string,
  to: string,
): ReturnType<typeof useApiQuery<ManagementDashboardResponse>> {
  const qs = new URLSearchParams({ from, to }).toString();
  return useApiQuery<ManagementDashboardResponse>(apiOpts, `/v1/dashboards/management?${qs}`, {
    queryKey: ['dashboards', 'management', from, to] as const,
    refetchInterval: 5 * 60_000,
  });
}
